import React, { useEffect, useState } from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import { Button, Row, Col, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { FormikInput, FormikSelect } from '../../../components';
import { debounceIndexEmployeesRequest } from '../../../requests/employees';
import { debounceIndexCostCentersRequest } from '../../../requests/costCenters';
import { debounceIndexJobManagementsRequest } from '../../../requests/jobManagements';
import { debounceIndexBranchOfficesRequest } from '../../../requests/branchOffices';

const Filters = props => {
  const dispatch = useDispatch();
  const { errors, touched, setFieldValue, values, onHide, setFieldTouched, isLoading } = props;
  const [costCenters, setCostCenters] = useState([]);
  const [jobManagement, setJobManagement] = useState([]);
  const [branchOffices, setBranchOffices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const { currentCompany } = useSelector(state => state.auth);
  const {
    filterActiveCostCenters,
    filterActiveJobManagements,
    filterActiveBranchOffices,
    employeeIds,
    filterNationalId
  } = values;

  const [isFiltering, setIsFiltering] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [placeholder, setPlaceholder] = useState('');
  const ALL_SELECTED_LABEL = 'Todos';

  const fetchEmployees = (costCentersIds = '', jobManagementsIds = '', branchOfficesIds = '') => {
    setIsFiltering(true);
    setDisabled(true);
    setPlaceholder('Cargando Colaboradores...');
    debounceIndexEmployeesRequest({
      dispatch,
      params: {
        active: true,
        active_contracts: true,
        sort_column: 'name',
        is_dt: false,
        company_id: currentCompany.id,
        filterActiveCostCenters: costCentersIds,
        filterActiveBranchOffices: branchOfficesIds,
        filterActiveJobManagements: jobManagementsIds,
        paginate: false
      },
      successCallback: response => {
        const dataToGroupSearch = [{ id: 'Todos', label: 'Todos', value: 'Todos' }, ...response.data.data];
        setEmployees(response.data.data.length > 0 ? dataToGroupSearch : []);
        setIsFiltering(false);
        setDisabled(false);
        setPlaceholder('Seleccione Colaboradores');
        setFieldValue('companyId', currentCompany.id);
      }
    });
  };

  const resultFetchData = (response, type) => {
    const result = response.data.data;
    return result.map(element => ({
      ...element,
      label: `${type ? `${element.code} - ` : ''} ${element.name}`,
      value: element.id
    }));
  };

  const fetchCostCenters = () => {
    debounceIndexCostCentersRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: data => {
        setCostCenters(resultFetchData(data));
      }
    });
  };

  const fetchJobManagments = () => {
    debounceIndexJobManagementsRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: data => {
        setJobManagement(resultFetchData(data));
      }
    });
  };

  const fetchBranchOffices = () => {
    debounceIndexBranchOfficesRequest({
      dispatch,
      params: {
        actives: true,
        sort_column: 'name',
        paginate: false
      },
      successCallback: data => {
        setBranchOffices(resultFetchData(data));
      }
    });
  };

  const handleNewMulti = (data, field, allowEmpty = false) => {
    const newData = data.map(element => element.value);
    if (allowEmpty && !newData.length) newData.push();
    setFieldValue(field, newData);
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.label === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.label === ALL_SELECTED_LABEL;
  };

  const disabledButton = employeeIds.length > 0 || employeeIds === '' || filterNationalId.length > 0;
  useEffect(fetchCostCenters, []);
  useEffect(fetchBranchOffices, []);
  useEffect(fetchJobManagments, []);
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line
  }, []);

  return (
    <Form>
      <Card style={{ zIndex: 'unset' }}>
        <Row>
          <Col className="mt-2">
            <Card className="card-dt">
              <Col className="top-header-green-dt card-header-title-dt pt-1 pb-1">Busqueda por empleado</Col>
              <Card.Body className="div-content card-body-padding">
                <Row>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="filterNationalId">
                      {({ field }) => (
                        <FormikInput
                          {...field}
                          label="RUT"
                          placeholder="Sin puntos y sin guion"
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                          disabled={
                            filterActiveCostCenters ||
                            filterActiveJobManagements ||
                            filterActiveBranchOffices ||
                            employeeIds.length > 0 ||
                            employeeIds === ''
                          }
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={8} xs={12}>
                    <Field name="employees">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Nombres y Apellidos"
                          isMulti
                          options={employees}
                          placeholder={placeholder}
                          isLoading={isFiltering}
                          onChange={data => {
                            handleNewMulti(data || [], 'employeeIds', true);
                            setFieldValue(field.name, data || []);
                            setFieldValue('employee', '');
                            setFieldValue('employee_id', '');
                          }}
                          isDisabled={isFiltering || filterNationalId}
                          isOptionDisabled={handleDisabledSelection}
                          isClearable
                          setFieldTouched={() => setFieldTouched(field.name)}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="filterActiveCostCenters">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Centro de Costos"
                          placeholder="Seleccionar Centro de Costos"
                          options={costCenters}
                          isClearable
                          onChange={data => {
                            const costCenterValue = data ? data.value : '';
                            setFieldValue(field.name, costCenterValue);
                            setFieldValue('employees', []);
                            setFieldValue('employeeIds', []);
                            fetchEmployees(costCenterValue, filterActiveJobManagements, filterActiveBranchOffices);
                          }}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                          isDisabled={filterActiveJobManagements || filterActiveBranchOffices}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="filterActiveJobManagements">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Área"
                          placeholder="Seleccionar Área"
                          options={jobManagement}
                          isClearable
                          isDisabled={disabledButton}
                          onChange={data => {
                            const jobManagementData = data ? data.value : '';
                            setFieldValue(field.name, jobManagementData);
                            // fetchEmployees(filterActiveCostCenters, jobManagementData, filterActiveBranchOffices);
                          }}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                  <Col sm={12} md={4} xs={12}>
                    <Field name="filterActiveBranchOffices">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          label="Lugar de Prestación de Servicios"
                          placeholder="Seleccionar Lugar de Prestación de Servicios"
                          options={branchOffices}
                          isClearable
                          isDisabled={disabledButton}
                          onChange={data => setFieldValue(field.name, data ? data.value : '')}
                          error={getIn(errors, field.name)}
                          touched={getIn(touched, field.name)}
                        />
                      )}
                    </Field>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card>
      <Row className="d-flex justify-content-end mt-4 mb-4">
        <Col md={2}>
          <Button className="btn-block" type="submit" onClick={onHide} disabled={disabled || isLoading}>
            Filtrar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const validationSchema = Yup.object().shape({
  employees: Yup.string().test({
    name: 'employees',
    message: 'Este campo es obligatorio',
    test(value) {
      const filterNationalId = this.parent.filterNationalId || '';
      const centerCost = this.parent.filterActiveCostCenters || '';
      const jobManagement = this.parent.filterActiveJobManagements || '';
      const branchOffice = this.parent.filterActiveBranchOffices || '';
      return !filterNationalId && !centerCost && !jobManagement && !branchOffice ? !!value : true;
    }
  }),
  filterNationalId: Yup.string().test({
    name: 'filterNationalId',
    message: 'Este campo es obligatorio',
    test(value) {
      const employeeIds = this.parent.employeeIds || '';
      const centerCost = this.parent.filterActiveCostCenters || '';
      const jobManagement = this.parent.filterActiveJobManagements || '';
      const branchOffice = this.parent.filterActiveBranchOffices || '';
      return !employeeIds && !centerCost && !jobManagement && !branchOffice ? !!value : true;
    }
  }),
  filterActiveCostCenters: Yup.string().test({
    name: 'filterActiveCostCenters',
    message: 'Este campo es obligatorio',
    test(value) {
      const employeeIds = this.parent.employeeIds || '';
      const filterNationalId = this.parent.filterNationalId || '';
      const jobManagement = this.parent.filterActiveJobManagements || '';
      const branchOffice = this.parent.filterActiveBranchOffices || '';
      return !employeeIds && !filterNationalId && !jobManagement && !branchOffice ? !!value : true;
    }
  }),
  filterActiveJobManagements: Yup.string().test({
    name: 'filterActiveJobManagements',
    message: 'Este campo es obligatorio',
    test(value) {
      const employeeIds = this.parent.employeeIds || '';
      const filterNationalId = this.parent.filterNationalId || '';
      const centerCost = this.parent.filterActiveCostCenters || '';
      const branchOffice = this.parent.filterActiveBranchOffices || '';
      return !employeeIds && !filterNationalId && !centerCost && !branchOffice ? !!value : true;
    }
  }),
  filterActiveBranchOffices: Yup.string().test({
    name: 'filterActiveBranchOffices',
    message: 'Este campo es obligatorio',
    test(value) {
      const employeeIds = this.parent.employeeIds || '';
      const filterNationalId = this.parent.filterNationalId || '';
      const centerCost = this.parent.filterActiveCostCenters || '';
      const jobManagement = this.parent.filterActiveJobManagements || '';
      return !employeeIds && !filterNationalId && !centerCost && !jobManagement ? !!value : true;
    }
  })
});

const setInitialValues = () => {
  return {
    employees: [],
    employee: '',
    employeeIds: [],
    filterNationalId: '',
    format: '',
    filterActiveCostCenters: '',
    filterActiveJobManagements: '',
    filterActiveBranchOffices: '',
    companyId: ''
  };
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  const { employeeIds } = values;

  formRequest({
    ...values,
    employeeIds: employeeIds.filter(item => item === 'Todos').length > 0 ? [] : employeeIds
  });
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true,
  validateOnMount: false
})(Filters);
